import { useEffect, useState } from "react"
//components
import Label from "@components/ui/labels/Label"
import { SelectContainer } from "@components/ui/layout/SelectContainer"
import Select from 'react-select'
import { AltData } from "./AltData"
import { Button } from "@components/ui/buttons/Button"
//hooks/context
import useFetchPoiOptions from "@hooks/useFetchPoiOptions"
import { useLoading } from "@contexts/LoadingContext"
import { useError } from "@contexts/ErrorContext"
import { useSuccess } from "@contexts/SuccessContext"
import useConfirmDialog from "@hooks/useConfirmDialog"
import { useUser } from "@contexts/UserContext"
import { useUnsavedChanges } from "@contexts/UnsavedChangesContext"
//api
import { 
    updatePOIGroupAPI, 
    addPOIsToGroupAPI,
    removePOIsFromGroupAPI
} from '@services/poiGroupsAPI'

/**
 * Component for PoiGroups tab that allows group editors to add or remove POIs, and update the linkedPOI names and descriptions
 * @param {Object} groupDetails group data including POIGroupID for api calls
 * @param {Boolean} isEditor determines if inputs are disabled or enabled
 * @param {Object} selectedJob to fetch POIs for job to fill dropdown
 * 
 * @returns {JSX.Element}
 */

export const UpdatePoisInGroup = ({groupDetails, isEditor, selectedJob}) => {
    const [altData, setAltData] = useState([])
    
    const {showLoading, hideLoading}  = useLoading()
    
    const {fetchPoiOptionsByJob, fetchPoiOptionsByGroup, poiNameOptions, poisInGroup, poisInGroupOriginal, setPoisInGroup, setPoisInGroupOriginal } = useFetchPoiOptions()
    const {showError} = useError()
    const {timeoutSuccess} = useSuccess()
    const [ConfirmDialog, requestConfirm] = useConfirmDialog();
    const {user} = useUser();
    const {setHasUnsavedChanges} = useUnsavedChanges()

    useEffect(() => {
        if(groupDetails){
            fetchPoiOptionsByGroup(groupDetails.POIGroupID)

        } else {
            setPoisInGroup([])
            setPoisInGroupOriginal([]) 
        }

        setAltData([])

    }, [groupDetails])
        
    useEffect(() => {

        fetchPoiOptionsByJob(selectedJob.value)
    }, [selectedJob]);

//    handlers
           const handleUpdatePois = async() => {
            showLoading("Submitting...")
    
            // get ids from current and original groups
            const updatedPOIs = poisInGroup.map(poi => poi.value ? {POIID: poi.value} : null).filter(Boolean)
            const originalPOIs = poisInGroupOriginal.map(poi => poi.value ? {POIID: poi.value} : null).filter(Boolean)
        
            // compare ids between current and original poi groups to determine which to add and remove
            const poisToAdd = poisInGroup
                .filter(poi => !originalPOIs.some(originalPoi => originalPoi.POIID === poi.value))
                .map(poi => {
    
                    if(poi){
                        // check if alt name or description was added, and if so, include in request
                        const alt = altData.find(alt => alt.POIID === poi.value)
    
                        return alt ? {
                            POIID: poi.value, 
                            POI_Name: alt.POI_Name,
                            Description: alt.Description
                        } : 
                        {POIID: poi.value}
                    }
    
                    return null
                })
                .filter(Boolean)
        
            const poisToRemove = poisInGroupOriginal
                .filter(originalPoi => !updatedPOIs.some(poi => poi.POIID === originalPoi.value))
                .map(originalPoi => originalPoi ? { POIID: originalPoi.value } : null)
                .filter(Boolean)
    
            // call api to add new poi, if any
            if(poisToAdd.length > 0){
                const response = await addPOIsToGroupAPI(groupDetails.POIGroupID, poisToAdd)
                        
                if(!response.success){
                    hideLoading()
    
                    showError(`Error adding pois: ${response.message}`)
            
                    return
                }
            }
    
            //if any alt names or descriptions changed and update them
            const linkedPOIsToUpdate = altData.filter(alt => alt.LPOIID)
    
            if(linkedPOIsToUpdate.length > 0){
                const response = await updatePOIGroupAPI(
                    groupDetails.POIGroupID, 
                    {
                        linkedPOIs: linkedPOIsToUpdate
                    }
                )
                        
                if(!response.success){
                    hideLoading()
    
                    showError(`Error saving alt names: ${response.message}`)
            
                    return
                }
            }
    
            // call api to remove poi, if any
            if(poisToRemove.length > 0){
                //confirm dialog to let user know that notifications are being remove for these pois
                const isConfirmed = await requestConfirm('Removing POIs From Your Group Will Also Remove Any Notifications That Are Associated', 'Continue', 'Cancel');
                    
                if(isConfirmed){
                    const response = await removePOIsFromGroupAPI(groupDetails.POIGroupID, poisToRemove, user.UserID)
                        
                    if(!response.success){
                        hideLoading()
        
                        showError(`Error removing pois: ${response.message}`)
                
                        return
                    } 
    
                    //re-fetch pois after deletion (refreshes their linkedPOIs)
                    fetchPoiOptionsByGroup(groupDetails.POIGroupID)
                };
            }
    
            //update original state to reflect new changes
            setPoisInGroupOriginal(poisInGroup)
            
            //remove alt names since they have been handled
            setAltData([])
            
            hideLoading()
            
            //everything submitted successfully, so let user know
            timeoutSuccess("Saved")
    
            setHasUnsavedChanges(false)
            
        }
    
        const handleAltDataChange = (updates, POIID, linkedPOI) => {

            setAltData(prevData => {
              // Find the index of the existing entry with the given POIID
              const existingIndex = prevData.findIndex(item => item.POIID === POIID);
          
              // Create a new object with existing data (if any), and apply the updates
              const newAltDataObject = {
                LPOIID: linkedPOI?.LPOIID ?? null,
                POIGroupID: groupDetails.POIGroupID,
                POIID,
                ...(existingIndex !== -1 ? prevData[existingIndex] : {}), // Preserve existing data if it exists
                ...updates // Apply updates dynamically
              };
          
              if (existingIndex === -1) {
                // If it doesn't exist yet, add a new item
                return [...prevData, newAltDataObject];
              } else {
                // If it does exist, replace it with the updated object
                return prevData.map((item, index) =>
                  index === existingIndex ? newAltDataObject : item
                );
              }
            });

            setHasUnsavedChanges(true)

          };
          
    
        const handleUpdatePOISelection = (selectedPOIs) => {
            //update selected poi state
            setPoisInGroup(selectedPOIs)
            
            //if poi is in altdata state but not in selectedPOIs, remove it from altData
            setAltData(prevData =>
                prevData.filter(alt =>
                    selectedPOIs.some(selected => selected.value === alt.POIID)
                )
            );

            setHasUnsavedChanges(true)

        }
    
    return(
        <div className="my-10">
            <hr className='mb-10 mt-2'/>
        
            {/* poi multiselect */}
            <Label>POIs in group</Label>
            <SelectContainer>
                <Select
                    value={poisInGroup}
                    options={poiNameOptions}
                    name="poiDropdown"
                    onChange={(x) => handleUpdatePOISelection(x)}
                    menuPosition='fixed'
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    isMulti={true}
                    isDisabled={!isEditor}
                />
            </SelectContainer>
        
            {/* let user assign alternate POI names for POIs in this group (if any POIs have been added to group) */}
            {poisInGroup.length > 0 && <Label className='mb-6'>Choose alternate names and descriptions for the POIs (optional)</Label>}
        
            {poisInGroup.length > 0 && poisInGroup.map(poi => (
                <AltData 
                    poi={poi}
                    poiGroupID={groupDetails.POIGroupID}
                    handleAltDataChange={handleAltDataChange}
                    key={`${poi.value}-${poi.label}`}
                    isDisabled={!isEditor}
                />
            ))}
        
            {/* update button - if user is editor only */}
            {isEditor ? (
                <div>
                    <Button variant="success" onClick={() => handleUpdatePois()}>Update POIs In Group</Button>
                </div>
            ) : null}
            
        {/* dialog box to confirm  POIs removed from group */}
        <ConfirmDialog />

    </div>
    )

}